import { queryOptions, useQuery } from '@tanstack/react-query';

import { getQueryClient } from '@/api/queryClient';
import { COUNTRY_COOKIE } from '@/app/consts/cookies';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import logToDatadog from '@/logger/logToDatadog';
import { getCookie } from '@/utils/cookie';
import gqlClient from '@/utils/gqlClient';

export const query = graphql(`
  query SellToCountry($countryCode: String) {
    content {
      countries(countryCode: $countryCode) {
        currency
        countryCode
        eu
      }
    }
  }
`);

export type SellToCountryResult = ResultOf<typeof query>;
export type SellToCountryVariables = VariablesOf<typeof query>;

export const FALLBACK_SELL_TO_COUNTRY: SellToCountryResult = {
  content: {
    countries: [
      {
        countryCode: 'US',
        currency: 'USD',
        eu: false,
      },
    ],
  },
} as const;

export const sellToCountryOptions = (vars: SellToCountryVariables) => {
  const queryKey = ['SellToCountry', { vars }];

  return queryOptions({
    queryKey,
    queryFn: async () => {
      try {
        const data = await gqlClient().request(query, vars);

        const countries = (data.content?.countries ?? []).filter((c) =>
          Boolean(c),
        );

        if (countries.length !== 1 || !countries[0]) {
          logToDatadog({
            data: {
              ...data.content,
              vars,
            },
            level: 'error',
            message: `Expected exactly one country for country code ${vars.countryCode}, but got ${countries.length}`,
            module: 'countries',
          });
        } else {
          return countries[0];
        }
      } catch (error) {
        logToDatadog({
          data: {
            error,
            vars,
          },
          level: 'error',
          message: `SellToCountry GraphQL query failed for country code ${vars.countryCode}`,
          module: 'countries',
        });
      }

      // In the event that no country is found, fallback to US after logging error(s).
      return FALLBACK_SELL_TO_COUNTRY.content!.countries![0]!;
    },
    initialData: () => {
      const queryClient = getQueryClient();
      return queryClient.getQueryData(queryKey);
    },
  });
};

export function useSellToCountry(countryCode?: string) {
  const countryCodeCookie = getCookie(COUNTRY_COOKIE);
  const _countryCode = countryCode ?? countryCodeCookie ?? 'US';
  return useQuery(sellToCountryOptions({ countryCode: _countryCode }));
}
